// Light/dark theme. Dark is the default palette (the @theme tokens in
// index.css); light overrides the same tokens under :root[data-theme="light"].
// index.html applies the stored / preferred theme before first paint; this
// hook keeps React in step with it and persists explicit choices.

import { useCallback, useState } from "react";

export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "calicool.theme";

function readStored(): Theme | null {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

function prefersLight(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: light)").matches
  );
}

export function getInitialTheme(): Theme {
  const attr = document.documentElement.dataset.theme;
  if (attr === "light" || attr === "dark") return attr;
  return readStored() ?? (prefersLight() ? "light" : "dark");
}

export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
}

export function useTheme(): { theme: Theme; toggle: () => void } {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  const toggle = useCallback(() => {
    setTheme((prev) => {
      const next: Theme = prev === "light" ? "dark" : "light";
      applyTheme(next);
      try {
        localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // Storage unavailable (private mode): the theme still applies for this visit.
      }
      return next;
    });
  }, []);

  return { theme, toggle };
}
